import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toPlainText } from '@portabletext/react';
import {
  useUpdateNotification,
  pickLaunch,
  getCardUpdates,
  getLaunchAction,
} from '@dashboard/hooks/useUpdateNotification';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import SanityImage from '@dashboard/lib/sanity/SanityImage';
import { Button as ShadcnButton } from '@dashboard/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from '@dashboard/components/ui/dialog';

// One launch per page load: a refresh shows the next one, moving between pages does not.
let launchForThisLoad = null;

const updateLink = update => `/updates#${update._id}`;
const findImage = update => update.body?.find(block => block._type === 'image');

export default function WhatsNew() {
  const { user } = useAuth();
  const { isReady, unseenUpdates, markUpdateAsSeen } = useUpdateNotification();

  if (!isReady) return null;

  if (launchForThisLoad?.userId !== user?._id) {
    launchForThisLoad = { userId: user?._id, launchId: pickLaunch(unseenUpdates)?._id ?? null };
  }

  if (launchForThisLoad.launchId) {
    const launch = unseenUpdates.find(update => update._id === launchForThisLoad.launchId);
    if (!launch) return null;
    return <LaunchDialog launch={launch} onDismiss={() => markUpdateAsSeen(launch._id)} />;
  }

  const cardUpdates = getCardUpdates(unseenUpdates);
  if (!cardUpdates.length) return null;

  return <UpdatesCard updates={cardUpdates} onDismiss={markUpdateAsSeen} />;
}

function UpdatesCard({ updates, onDismiss }) {
  const [index, setIndex] = useState(0);
  const current = Math.min(index, updates.length - 1);
  const update = updates[current];
  const image = findImage(update);

  return (
    <div className="mb-6 border border-gray-200 dark:border-border rounded-lg relative p-4">
      <div className="absolute top-2 right-2">
        <ShadcnButton
          variant="ghost"
          size="icon"
          aria-label="Close this update"
          onClick={() => onDismiss(update._id)}
        >
          <X className="h-5 w-5" />
        </ShadcnButton>
      </div>

      {updates.length > 1 && (
        <div className="absolute bottom-2 right-2 flex items-center gap-1">
          <ShadcnButton
            variant="ghost"
            size="icon"
            aria-label="Previous update"
            disabled={current === 0}
            onClick={() => setIndex(current - 1)}
          >
            <ChevronLeft className="h-5 w-5" />
          </ShadcnButton>
          <span className="text-sm text-gray-600 dark:text-muted-foreground tabular-nums">
            {current + 1} of {updates.length}
          </span>
          <ShadcnButton
            variant="ghost"
            size="icon"
            aria-label="Next update"
            disabled={current === updates.length - 1}
            onClick={() => setIndex(current + 1)}
          >
            <ChevronRight className="h-5 w-5" />
          </ShadcnButton>
        </div>
      )}

      <div className="flex items-center gap-x-4 h-full">
        {image && (
          <SanityImage
            key={update._id}
            asset={image.asset}
            size={512}
            alt={update.title}
            className="hidden md:block w-3xs shrink-0 rounded-lg"
          />
        )}

        <div className="flex flex-col max-w-xl pr-10">
          <h3 className="text-base font-semibold mb-0.5">{update.title}</h3>
          <p className="text-gray-600 dark:text-muted-foreground text-sm line-clamp-2 mb-2">
            {toPlainText(update.body)}
          </p>

          <div>
            <Link to={updateLink(update)} onClick={() => onDismiss(update._id)}>
              <ShadcnButton variant="outline" size="sm">
                Learn more
              </ShadcnButton>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

function LaunchDialog({ launch, onDismiss }) {
  const image = findImage(launch);
  const action = getLaunchAction(launch);

  return (
    <Dialog open onOpenChange={open => !open && onDismiss()}>
      <DialogContent className="w-[calc(100%-2rem)] max-w-xl gap-0 overflow-hidden rounded-lg p-0 [&>button]:rounded-full [&>button]:bg-background [&>button]:p-1.5 [&>button]:opacity-90">
        {image && (
          <SanityImage asset={image.asset} size={1200} alt={launch.title} className="w-full" />
        )}
        <div className="px-6 pt-5">
          <DialogTitle className="text-xl leading-tight">{launch.title}</DialogTitle>
          <DialogDescription className="mt-2 line-clamp-3">
            {toPlainText(launch.body)}
          </DialogDescription>
        </div>
        <DialogFooter className="gap-2 px-6 pb-6 pt-5">
          <ShadcnButton variant="outline" onClick={onDismiss}>
            Close
          </ShadcnButton>
          <ShadcnButton asChild>
            {action.to ? (
              <Link to={action.to} onClick={onDismiss}>
                {action.label}
              </Link>
            ) : (
              <a href={action.href} target="_blank" rel="noopener noreferrer" onClick={onDismiss}>
                {action.label}
              </a>
            )}
          </ShadcnButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
